import * as React from 'react';
import { cn } from '@/lib/utils';

export interface StatProps extends React.HTMLAttributes<HTMLDivElement> {
  value: React.ReactNode;
  label?: React.ReactNode;
  unit?: React.ReactNode;
  delta?: React.ReactNode;
  deltaTone?: 'ok' | 'bad' | 'neutral';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Stat — large mono numeric with optional unit, delta, and label.
 * Used in cohort cards, dashboard metrics, evidence verdicts.
 */
export const Stat = React.forwardRef<HTMLDivElement, StatProps>(
  ({ value, label, unit, delta, deltaTone = 'ok', size = 'md', className, ...rest }, ref) => {
    const sizeMap = {
      sm: { num: 'text-[20px]', unit: 'text-[11px]' },
      md: { num: 'text-[28px]', unit: 'text-[12px]' },
      lg: { num: 'text-[36px]', unit: 'text-[13px]' },
    };
    const tone =
      deltaTone === 'bad' ? 'text-fd-bad' : deltaTone === 'neutral' ? 'text-fd-text-subtle' : 'text-fd-ok';
    return (
      <div ref={ref} className={cn('flex flex-col gap-1', className)} {...rest}>
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              'font-geist-mono font-medium leading-none tracking-[-0.02em] text-fd-text',
              sizeMap[size].num,
            )}
          >
            {value}
          </span>
          {unit && (
            <span className={cn('font-geist-mono text-fd-text-subtle', sizeMap[size].unit)}>{unit}</span>
          )}
          {delta && (
            <span className={cn('ml-auto font-geist-mono text-[12px]', tone)}>{delta}</span>
          )}
        </div>
        {label && (
          <span className="font-geist-mono text-[10.5px] uppercase tracking-[0.06em] text-fd-text-subtle">
            {label}
          </span>
        )}
      </div>
    );
  },
);
Stat.displayName = 'Stat';
